// Minimap rendered with Google Maps JS API (already loaded by searchUI for
// PlaceAutocompleteElement). The aircraft is shown as a rotating arrow kept
// at the map centre; its lat/lng is computed by the caller (game.js) with the
// ellipsoid-exact ENU conversion from geo.js, so the map matches the 3D tiles
// at any distance from the departure city.

export async function createMinimap({ centerLat, centerLng }) {
  if (!window.google?.maps?.importLibrary) {
    throw new Error('Google Maps JS API not loaded yet');
  }
  // GoogleMap にリネームしておく（素の `Map` のままだと、この関数スコープ全体で
  // 組み込みの Map が覆い隠されてしまう）。
  const { Map: GoogleMap } = await google.maps.importLibrary('maps');
  const { AdvancedMarkerElement } = await google.maps.importLibrary('marker');

  const container = document.getElementById('minimap');
  if (!container) throw new Error('#minimap container not found');
  container.innerHTML = '';

  const map = new GoogleMap(container, {
    center: { lat: centerLat, lng: centerLng },
    zoom: 13,
    disableDefaultUI: true,
    zoomControl: true,        // re-enable + / − buttons
    gestureHandling: 'greedy', // pinch / scroll-wheel zoom without modifier
    keyboardShortcuts: false,
    clickableIcons: false,
    mapId: 'DEMO_MAP_ID',
  });

  // City-centre dot
  const centerEl = document.createElement('div');
  centerEl.className = 'minimap-center-dot';
  const centerMarker = new AdvancedMarkerElement({
    map,
    position: { lat: centerLat, lng: centerLng },
    content: centerEl,
  });

  // Aircraft arrow marker
  const arrowEl = document.createElement('div');
  arrowEl.className = 'minimap-aircraft';
  arrowEl.innerHTML =
    '<svg viewBox="0 0 16 20" xmlns="http://www.w3.org/2000/svg">' +
    '<polygon points="8,0 16,20 8,15 0,20" fill="#ffeb3b" stroke="#000" stroke-width="1"/>' +
    '</svg>';
  const aircraftMarker = new AdvancedMarkerElement({
    map,
    position: { lat: centerLat, lng: centerLng },
    content: arrowEl,
  });

  // Expand / collapse toggle (top-right corner of minimap).
  const expandBtn = document.createElement('button');
  expandBtn.type = 'button';
  expandBtn.className = 'minimap-expand-btn';
  expandBtn.title = '拡大表示';
  expandBtn.textContent = '⤢';
  expandBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    const expanded = container.classList.toggle('expanded');
    expandBtn.title = expanded ? '元に戻す' : '拡大表示';
    expandBtn.textContent = expanded ? '⤡' : '⤢';
    // ResizeObserver below catches the size change and re-lays-out the map.
  });
  container.appendChild(expandBtn);

  // Last known aircraft position — used to re-centre after a resize so the
  // expand/collapse toggle doesn't snap the view back to the departure city.
  let lastPos = { lat: centerLat, lng: centerLng };
  let lastHeadingRad = null;

  // If the container is ever resized (e.g. by a CSS class change), the
  // Google Map needs to recompute its internal layout.
  let resizeObserver = null;
  if (typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver(() => {
      if (google.maps?.event?.trigger) {
        google.maps.event.trigger(map, 'resize');
      }
      map.setCenter(lastPos);
    });
    resizeObserver.observe(container);
  }

  // 世界史スポットのマーカー。渡されるのは historicRings が持つ近傍セット
  // （数件）だけで、その顔ぶれが変わったときにしか呼ばれないので、素直に
  // 作り直す。ズーム13 の表示域は 3〜4km 程度しかない。
  let siteMarkers = [];

  function setSites(sites) {
    for (const m of siteMarkers) {
      try { m.map = null; } catch {}
    }
    siteMarkers = sites.map((s) => {
      const el = document.createElement('div');
      el.className = 'minimap-site' + (s.visited ? ' visited' : '') + (s.target ? ' target' : '');
      el.title = (s.target ? '次の目的地: ' : '') + s.name;
      return new AdvancedMarkerElement({
        map,
        position: { lat: s.lat, lng: s.lng },
        content: el,
      });
    });
  }

  function update({ lat, lng, headingRad }) {
    // 静止中（駐機・滑走待ち）は setCenter / transform 書き換えをスキップ。
    if (lat === lastPos.lat && lng === lastPos.lng && headingRad === lastHeadingRad) {
      return;
    }
    lastHeadingRad = headingRad;
    const pos = { lat, lng };
    lastPos = pos;
    aircraftMarker.position = pos;
    // Pan the map so the aircraft stays at the centre of the minimap.
    // (setCenter is instant — no animation overhead at 15Hz.)
    map.setCenter(pos);
    const bearingDeg = (headingRad * 180) / Math.PI;
    arrowEl.style.transform = `rotate(${bearingDeg}deg)`;
  }

  function dispose() {
    try { resizeObserver?.disconnect(); } catch {}
    try { aircraftMarker.map = null; } catch {}
    try { centerMarker.map = null; } catch {}
    for (const m of siteMarkers) {
      try { m.map = null; } catch {}
    }
    siteMarkers = [];
    if (container) container.innerHTML = '';
  }

  return { update, setSites, dispose };
}
